import {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { Snackbar, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
  getPoiFavouritesFromLocalStorage,
  setPoiFavouritesFromLocalStorage,
  getHeatmapFavouritesFromLocalStorage,
  setHeatmapFavouritesFromLocalStorage,
} from '../components/Favourites/favourites-local-storage';

// Split contexts for better performance
type FavouritesDataContextType = {
  poiFavourites: string[];
  heatmapFavourites: string[];
};

type FavouritesActionsContextType = {
  togglePoiFavourite: (studyId: string) => void;
  toggleHeatmapFavourite: (countryCode: string) => void;
  isPoiFavourited: (studyId: string) => boolean;
  isHeatmapFavourited: (countryCode: string) => boolean;
  clearPoiFavourites: () => void;
  clearHeatmapFavourites: () => void;
};

export const FavouritesDataContext =
  createContext<FavouritesDataContextType | null>(null);
export const FavouritesActionsContext =
  createContext<FavouritesActionsContextType | null>(null);

interface Props {
  children: React.ReactNode;
}

export const useFavouritesData = () => {
  const context = useContext(FavouritesDataContext);

  if (!context) {
    throw new Error(
      'useFavouritesData: Used outside of the FavouritesProvider',
    );
  }

  return context;
};

export const useFavouritesActions = () => {
  const context = useContext(FavouritesActionsContext);

  if (!context) {
    throw new Error(
      'useFavouritesActions: Used outside of the FavouritesProvider',
    );
  }

  return context;
};

// Convenience hook for components that need both
export const useFavouritesContext = () => {
  return {
    ...useFavouritesData(),
    ...useFavouritesActions(),
  };
};

export function FavouritesProvider(props: Props) {
  const { t } = useTranslation();
  const [poiFavourites, setPoiFavourites] = useState<string[]>([]);
  const [heatmapFavourites, setHeatmapFavourites] = useState<string[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Load favourites from localStorage on mount
  useEffect(() => {
    setPoiFavourites(getPoiFavouritesFromLocalStorage());
    setHeatmapFavourites(getHeatmapFavouritesFromLocalStorage());
  }, []);

  const showSnackbar = (message: string) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const togglePoiFavourite = useCallback(
    (studyId: string) => {
      setPoiFavourites(prev => {
        const isFavourited = prev.includes(studyId);
        const updated = isFavourited
          ? prev.filter(id => id !== studyId)
          : [...prev, studyId];
        setPoiFavouritesFromLocalStorage(updated);
        showSnackbar(
          isFavourited
            ? t('favourites.removedFromFavourites')
            : t('favourites.addedToFavourites'),
        );
        return updated;
      });
    },
    [t],
  );

  const toggleHeatmapFavourite = useCallback(
    (countryCode: string) => {
      setHeatmapFavourites(prev => {
        const isFavourited = prev.includes(countryCode);
        const updated = isFavourited
          ? prev.filter(code => code !== countryCode)
          : [...prev, countryCode];
        setHeatmapFavouritesFromLocalStorage(updated);
        showSnackbar(
          isFavourited
            ? t('favourites.removedFromFavourites')
            : t('favourites.addedToFavourites'),
        );
        return updated;
      });
    },
    [t],
  );

  const isPoiFavourited = useCallback(
    (studyId: string) => poiFavourites.includes(studyId),
    [poiFavourites],
  );

  const isHeatmapFavourited = useCallback(
    (countryCode: string) => heatmapFavourites.includes(countryCode),
    [heatmapFavourites],
  );

  const clearPoiFavourites = useCallback(() => {
    setPoiFavourites([]);
    setPoiFavouritesFromLocalStorage([]);
  }, []);

  const clearHeatmapFavourites = useCallback(() => {
    setHeatmapFavourites([]);
    setHeatmapFavouritesFromLocalStorage([]);
  }, []);

  const dataValue = useMemo(
    () => ({
      poiFavourites,
      heatmapFavourites,
    }),
    [poiFavourites, heatmapFavourites],
  );

  const actionsValue = useMemo(
    () => ({
      togglePoiFavourite,
      toggleHeatmapFavourite,
      isPoiFavourited,
      isHeatmapFavourited,
      clearPoiFavourites,
      clearHeatmapFavourites,
    }),
    [
      togglePoiFavourite,
      toggleHeatmapFavourite,
      isPoiFavourited,
      isHeatmapFavourited,
      clearPoiFavourites,
      clearHeatmapFavourites,
    ],
  );

  return (
    <FavouritesDataContext.Provider value={dataValue}>
      <FavouritesActionsContext.Provider value={actionsValue}>
        {props.children}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity="success"
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </FavouritesActionsContext.Provider>
    </FavouritesDataContext.Provider>
  );
}
